import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { ChevronDown } from 'lucide-react';
import BrandSelect from '../BrandSelect';
import ContactSelect from '../ContactSelect';
import Combobox from '../Combobox';
import ContentStatusChecklist from '../ContentStatusChecklist';
import { getContentStatus, getContentStatusMeta, toggleContentStatus } from '../../lib/deals';
import { useDeals } from '../../context/DataContext';
import { STAGES } from '../../context/DataContext';

const QuickAddDeal = ({ open, onOpenChange, defaultStage = 'conversation' }) => {
  const { addDeal, brands, talent } = useDeals();
  const [talentId, setTalentId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [contactId, setContactId] = useState('');
  const [value, setValue] = useState('');
  const [stage, setStage] = useState(defaultStage);
  const [notes, setNotes] = useState('');
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [contentStatuses, setContentStatuses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setTalentId(''); setBrandId(''); setContactId('');
      setValue(''); setStage(defaultStage); setNotes('');
      setTitle(''); setTitleTouched(false); setContentStatuses([]);
      setError(null);
    }
  }, [open, defaultStage]);

  const selectedBrand = useMemo(() => brands.find((b) => b.id === brandId), [brands, brandId]);
  const selectedTalent = useMemo(() => talent.find((t) => t.user_id === talentId), [talent, talentId]);
  const canSave = !saving;

  const autoTitle = useMemo(() => {
    const brandName = selectedBrand?.name;
    const talentName = (selectedTalent?.name || '').split(' ')[0];
    if (brandName && talentName) return `${brandName} x ${talentName}`;
    if (brandName) return `${brandName} deal`;
    if (talentName) return `${talentName} deal`;
    return '';
  }, [selectedBrand, selectedTalent]);

  useEffect(() => {
    if (!titleTouched) setTitle(autoTitle);
  }, [autoTitle, titleTouched]);

  const toggleStatus = (id) => {
    const patch = toggleContentStatus({ content_statuses: contentStatuses }, id);
    setContentStatuses(patch.content_statuses);
  };

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: title.trim() || autoTitle || 'New deal',
        talent_id: talentId || null,
        brand_id: brandId || null,
        contact_id: contactId || null,
        stage: stage || defaultStage,
      };
      if (contentStatuses.length) {
        payload.content_statuses = contentStatuses;
        payload.content_status = contentStatuses[contentStatuses.length - 1];
      }
      const trimmedValue = String(value).trim();
      if (trimmedValue !== '') {
        const n = Number(trimmedValue);
        if (!Number.isNaN(n)) payload.value = n;
      }
      if ((notes || '').trim()) payload.notes = notes.trim();
      await addDeal(payload);
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add deal');
    } finally {
      setSaving(false);
    }
  };

  const talentItems = talent.map((t) => ({ value: t.user_id, label: t.name, sublabel: t.handle || t.email, keywords: [t.email, t.handle] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-800">new deal</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">fill in whatever's handy — everything can be tweaked later by clicking the card.</p>

        <div className="space-y-4 mt-3">
          <div>
            <Label>Deal name</Label>
            <Input
              value={title}
              onChange={(e) => { setTitle(e.target.value); setTitleTouched(true); }}
              placeholder="auto-fills from talent + brand — edit anytime"
              className="mt-1"
              data-testid="quick-deal-title"
            />
          </div>

          <div>
            <Label>Talent</Label>
            <Combobox
              value={talentId}
              onChange={setTalentId}
              items={talentItems}
              placeholder="Search talent…"
              emptyText="no talent match"
              triggerClassName="mt-1"
            />
          </div>

          <div>
            <Label>Brand</Label>
            <BrandSelect
              value={brandId}
              onChange={(v) => { setBrandId(v); setContactId(''); }}
              placeholder="Search or pick brand…"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Brand contact</Label>
            <div className="mt-1">
              <ContactSelect
                brand={selectedBrand}
                value={contactId}
                onChange={setContactId}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>£ rate <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                type="number"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. 1500"
                className="mt-1"
                data-testid="quick-deal-value"
              />
            </div>
            <div>
              <Label>Stage <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="mt-1" data-testid="quick-deal-stage"><SelectValue placeholder="pick stage" /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Content status <span className="text-slate-400 font-normal">(optional)</span></Label>
            <div className="mt-1">
              <Popover>
                <PopoverTrigger asChild>
                  {(() => {
                    const current = contentStatuses.length ? contentStatuses[contentStatuses.length - 1] : 'not_started';
                    const meta = getContentStatusMeta(current);
                    return (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium border border-slate-200 min-w-[160px]"
                        style={{ background: `${meta.color}14`, color: meta.color }}
                        data-testid="quick-deal-content-status"
                      >
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.color }} />
                        <span className="truncate">{meta.label || 'not started'}</span>
                        <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60 ml-auto" />
                      </button>
                    );
                  })()}
                </PopoverTrigger>
                <PopoverContent align="start" className="w-56 p-1">
                  <ContentStatusChecklist deal={{ content_statuses: contentStatuses }} isManager onToggle={toggleStatus} />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div>
            <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="anything you want to remember about this deal…"
              className="mt-1 text-sm"
              data-testid="quick-deal-notes"
            />
          </div>
        </div>

        {error && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}

        <DialogFooter className="mt-4 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button disabled={!canSave} onClick={submit} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold" data-testid="quick-deal-save">
            {saving ? 'adding…' : 'add deal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddDeal;
